import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoaderCircle } from 'lucide-react';
import * as yup from 'yup';

import { isOnboardingGateDisabled } from '@/api/onboarding/onboarding';
import AuthLayout from '@/component-library/layouts/AuthLayout/AuthLayout';
import { Button } from '@/component-library/primitives/Button/Button';
import CircleWarningIcon from '@/component-library/primitives/Icons/CircleWarningIcon/CircleWarningIcon';
import Link from '@/component-library/primitives/Link/Link';
import { Input } from '@/component-library/widgets/Input/Input';
import { AppRoutes } from '@/utils/routes/router';

import { useAuth } from '../../hooks/useAuth';

type FormData = {
  email: string;
  password: string;
};

/** Legacy Cognito / Amplify email+password sign-in. */
const CognitoSignInPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [signInError, setSignInError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    console.info('[ChurnSignal] Sign-in: AWS Cognito (Amplify).');
  }, []);

  const schema = useMemo(() => {
    return yup.object().shape({
      email: yup
        .string()
        .email(t('CommonErrors.emailFormatValidation'))
        .max(256)
        .required(t('CommonErrors.emailRequired')),
      password: yup.string().required(t('CommonErrors.passwordRequired')),
    });
  }, [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setSignInError('');

    try {
      const result = await auth.signIn(data.email, data.password);
      if (result.isSignInSuccess) {
        navigate(isOnboardingGateDisabled ? AppRoutes.dashboard : AppRoutes.onboardingForm);
      } else if (result.message === 'NEW_PASSWORD_REQUIRED') {
        navigate({ pathname: '/create-new-password' });
      } else {
        setSignInError(result.message);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      setSignInError(t('CommonErrors.incorrectUsernameOrPassword') || 'An error occurred during sign in');
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      {signInError && (
        <div className="mb-6 p-3 bg-custom-red/10 rounded-lg border border-custom-red/30">
          <p className="text-custom-red text-sm font-medium flex items-center gap-2">
            <CircleWarningIcon /> {signInError}
          </p>
        </div>
      )}

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col w-full space-y-6"
        data-testid="@auth/sign-in/form"
        data-auth-provider="cognito"
      >
        <Input
          placeholder={t('SignInPage.emailPlaceholder')}
          label={t('SignInPage.emailLabel')}
          {...register('email', { required: 'Email is required!' })}
          error={errors.email?.message}
          className="w-full"
          data-testid="@auth/sign-in/email-input"
          data-cy="userEmail"
        />

        <Input
          placeholder={t('SignInPage.passwordPlaceholder')}
          label={t('SignInPage.passwordLabel')}
          type="password"
          {...register('password', { required: 'Password is required!' })}
          error={errors.password?.message}
          className="w-full"
          data-testid="@auth/sign-in/password-input"
          data-cy="userPassword"
        />

        <div className="flex justify-end">
          <Link
            to={AppRoutes.forgotPassword}
            label={t('SignInPage.linkForgotPassword')}
            variant="primary"
            className="text-sm text-custom-blue hover:text-custom-darkBlue transition-colors no-underline"
          />
        </div>

        <Button
          data-testid="@auth/sign-in/submit-button"
          className="w-full bg-custom-blue hover:bg-custom-darkBlue text-white font-semibold py-3 rounded-lg transition-colors"
          data-cy="buttonSignInSubmit"
          disabled={isLoading}
        >
          {t('SignInPage.buttonSignIn')}
          {isLoading && <LoaderCircle height={20} className="animate-spin" />}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <div className="text-custom-darkSilver text-sm">
          {t('SignInPage.noAccount')}{' '}
          <Link
            className="ml-1 text-custom-blue hover:text-custom-darkBlue transition-colors no-underline"
            to={AppRoutes.signUp}
            label={t('SignInPage.registerHere')}
          />
        </div>
      </div>
    </AuthLayout>
  );
};

export default CognitoSignInPage;
